import { isEmpty, isNil } from 'ramda';
import * as yup from 'yup';

import { TimeSheetState } from 'src/enums/TimeSheet';
import { decimalHoursSchema } from 'src/forms/decimalHours';
import EngagementTimeSheet from 'src/types/resources/EngagementTimeSheet';
import { NestedTimeSheet } from 'src/types/resources/TimeSheet';
import { getMaxFieldLengthErrorMessage } from 'src/utils/validation';

export type TimeSheetsFormData = {
  talentComment: string;
  hours: string;
  purchaseOrderId: ID;
  isPurchaseOrderIdRequired: boolean;
};

export type TimeSheetsFormDataToSubmit = Omit<TimeSheetsFormData, 'isPurchaseOrderIdRequired'> & {
  engagementId: number;
  parentId: number;
  timekeepingDate: string;
};

export const validationSchema = yup.object({
  talentComment: yup.string().trim().max(500, getMaxFieldLengthErrorMessage(500)).default('').label('Comment'),
  hours: decimalHoursSchema.default(''),
  purchaseOrderId: yup
    .number()
    .nullable()
    .default(null)
    .when('isPurchaseOrderIdRequired', {
      is: (isPurchaseOrderIdRequired: boolean) => isPurchaseOrderIdRequired,
      then: schema => schema.required(),
    })
    .label('PO'),
  isPurchaseOrderIdRequired: yup.boolean().default(false),
});

export const initialValues = (
  engagementTimeSheet: EngagementTimeSheet,
  timeSheet?: NestedTimeSheet,
): TimeSheetsFormData => {
  const { po } = engagementTimeSheet;
  const isPurchaseOrderIdRequired = !isEmpty(po);
  const isSinglePO = po.length === 1;

  if (isNil(timeSheet) || timeSheet?.state === TimeSheetState.open) {
    return {
      ...validationSchema.getDefault(),
      purchaseOrderId: isSinglePO ? po[0]?.id : null,
      isPurchaseOrderIdRequired,
    };
  }

  const { talentComment, hours, purchaseOrder } = timeSheet;

  return {
    hours,
    talentComment: talentComment ?? '',
    purchaseOrderId: purchaseOrder?.id ?? null,
    isPurchaseOrderIdRequired,
  };
};

export const attributesToSubmit = (
  engagementId: number,
  timekeepingDate: string,
  values: TimeSheetsFormData,
  parentTimeSheetId?: number,
): TimeSheetsFormDataToSubmit => {
  const { hours, talentComment, purchaseOrderId } = values;

  return {
    engagementId,
    timekeepingDate,
    purchaseOrderId,
    parentId: parentTimeSheetId,
    hours,
    talentComment: isEmpty(talentComment) ? null : talentComment,
  };
};
